import React, { useContext, useState } from 'react';
import CreateChallenge from './CreateChallenge';
import Challenge from '../../Challenge';
import PhaseContext from '../../PhaseContext';
import PhaseColumn from './PhaseColumn';
import useChallengePhaseDrop from './useChallengePhaseDrop';
import RowContext from '../../Rows/Row/RowContext';
import ResponsiveMasonry from './ResponsiveMasonry';
import KanbanPhase from '@/lib/constants/KanbanPhase.enum';
import { type ChallengeSummary } from '@/lib/types';
import ColumnExpansionControls from './ColumnExpansionControls';
import useDefaultVisibleChallenges from './useDefaultVisibleChallenges';
import reverseSortByCreatedAt from '@/lib/utils/sorting/reverseSortByCreatedAt';

type KanbanPhaseColumnProps = {
  phase: KanbanPhase;
  challenges: ChallengeSummary[];
  collapsed: boolean;
};

// TODO memo should be applied on passed-in challenges, prevents a lot of
// unnecessary downstream computations and allow usage of downstream memo'ing
const KanbanPhaseColumn = ({
  phase,
  challenges: unsortedChallenges,
  collapsed,
}: KanbanPhaseColumnProps) => {
  const row = useContext(RowContext);
  const attachDropHandlingRef = useChallengePhaseDrop(phase, row);

  const [columnExpanded, setColumnExpanded] = useState(false);

  const challenges = unsortedChallenges.sort(reverseSortByCreatedAt);

  const defaultVisibleChallenges = useDefaultVisibleChallenges(challenges);
  const visibleChallenges = columnExpanded
    ? challenges
    : defaultVisibleChallenges;

  return (
    <PhaseContext.Provider value={phase}>
      <PhaseColumn
        hasItems={visibleChallenges.length > 0}
        collapsed={collapsed}
        ref={attachDropHandlingRef as unknown as React.Ref<HTMLDivElement>}
      >
        <ResponsiveMasonry collapsed={collapsed}>
          {visibleChallenges.map(challenge => (
            <Challenge
              challenge={challenge}
              collapsed={collapsed}
              key={challenge.id}
            />
          ))}
        </ResponsiveMasonry>
        {!collapsed &&
          challenges.length !== defaultVisibleChallenges.length && (
            <ColumnExpansionControls
              columnExpanded={columnExpanded}
              setColumnExpanded={setColumnExpanded}
              hiddenChallengesCount={
                challenges.length - defaultVisibleChallenges.length
              }
            />
          )}
        {!collapsed && phase === KanbanPhase.TODO && <CreateChallenge />}
      </PhaseColumn>
    </PhaseContext.Provider>
  );
};

export default KanbanPhaseColumn;
